"use client"

import React, { useEffect, useRef } from 'react'
import { motion, AnimatePresence, PanInfo } from 'framer-motion'
import { X } from 'react-feather'
import { createPortal } from 'react-dom'

interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  position?: 'bottom' | 'right'
  heightPercentage?: number
  showCloseButton?: boolean
  title?: string
  className?: string
  theme?: 'dark' | 'light'
}

const Drawer = ({ 
  isOpen, 
  onClose, 
  children, 
  position = 'bottom',
  heightPercentage = 80,
  showCloseButton = true,
  title,
  className = '',
  theme = 'dark'
}: DrawerProps) => {
  const light = theme === 'light'
  const drawerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    if (position === 'bottom') {
      if (info.offset.y > 100 || info.velocity.y > 500) {
        onClose()
      }
    } else if (position === 'right') {
      if (info.offset.x > 100 || info.velocity.x > 500) {
        onClose()
      }
    }
  }

  const getDrawerStyles = () => {
    if (position === 'bottom') {
      return {
        bottom: 0,
        left: 0,
        right: 0,
        height: `${heightPercentage}vh`,
        borderTopLeftRadius: '1.5rem',
        borderTopRightRadius: '1.5rem',
      }
    } else {
      return {
        top: 0,
        right: 0,
        bottom: 0,
        width: '90%',
        maxWidth: '400px',
        borderTopLeftRadius: '1.5rem',
        borderBottomLeftRadius: '1.5rem',
      }
    }
  }

  const getAnimationVariants = () => {
    if (position === 'bottom') {
      return {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' }
      }
    } else {
      return {
        initial: { x: '100%' },
        animate: { x: 0 },
        exit: { x: '100%' }
      }
    }
  }

  const getDragConstraints = () => {
    if (position === 'bottom') {
      return { top: 0, bottom: 0 }
    } else {
      return { left: 0, right: 0 }
    }
  }

  const getDragDirection = () => {
    return position === 'bottom' ? 'y' : 'x'
  }

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998]"
            aria-hidden="true"
            onClick={onClose}
          />

          <motion.div
            ref={drawerRef}
            {...getAnimationVariants()}
            transition={{ 
              type: 'spring', 
              damping: 30, 
              stiffness: 300 
            }}
            drag={getDragDirection()}
            dragConstraints={getDragConstraints()}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={`fixed shadow-2xl z-[99999] overflow-hidden ${light ? 'bg-white' : 'bg-black'} ${className}`}
            style={{
              ...getDrawerStyles(),
              border: light ? '1px solid rgb(229, 231, 235)' : '1px solid rgb(31, 41, 55)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full h-full flex flex-col">
              {position === 'bottom' && (
                <div className="flex items-center justify-center py-2 shrink-0">
                  <div className={`w-12 h-1.5 rounded-full ${light ? "bg-gray-300" : "bg-gray-700"}`} />
                </div>
              )}

              {(title || showCloseButton) && (
                <div className={`flex items-center justify-between px-5 py-4 border-b shrink-0 ${light ? "border-gray-100" : "border-gray-800"}`}>
                  {title && (
                    <h2 className={`text-sm font-semibold ${light ? "text-gray-900" : "text-gray-100"}`}>{title}</h2>
                  )}
                  {showCloseButton && (
                    <button
                      onClick={onClose}
                      aria-label="Close"
                      className={`p-2 rounded-lg transition-colors ml-auto ${light ? "text-gray-500 hover:bg-gray-100" : "text-gray-400 hover:bg-gray-800"}`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              )}

              <div className={`flex-1 min-h-0 ${light ? "overflow-y-auto" : "overflow-hidden"}`}>
                {children}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}

export default Drawer